import { CachedValue, SlotTools } from "@ingka/webc-tools";
import { Collapsible } from "@ingka/collapsible-webc/passive";
import SkapaElement from "@ingka/skapa-webc-element";
const styles = 'button{color:inherit;outline:none;user-select:text}:host{background:none;color:rgb(var(--colour-text-and-icon-2,72,72,72));display:flex;flex-direction:column;min-height:var(--skapa-choice-min-height,4.5rem);outline:0;position:relative;width:100%}.action{-webkit-tap-highlight-color:transparent;align-items:center;background:none;border:0;cursor:pointer;display:flex;flex:1;font-family:inherit;height:var(--skapa-choice-min-height,4.5rem);min-height:var(--skapa-choice-min-height,4.5rem);padding:var(--skapa-choice-padding-block,.75rem) 1.5rem;scroll-margin-top:var(--skapa-scroll-margin-top,inherit);text-decoration:none}.action:disabled{cursor:default}.action:disabled slot[name=caption]::slotted(*),.action:disabled slot[name=title]::slotted(*){color:rgb(var(--colour-interactive-disabled-1,204,204,204))!important}.action:disabled slot[name=addon]::slotted(*),.action:disabled slot[name=leading]::slotted(*),.action:disabled slot[name=trailing]::slotted(*){opacity:.2}@media (hover:hover){.action:hover:not(:disabled){text-decoration:none}}.action .content{align-items:center;display:flex;flex-direction:row;flex-grow:1;min-width:50%}.action .text-content{align-self:center;flex-grow:1;text-align:start}.action slot[name=title]::slotted(*){color:rgb(var(--colour-text-and-icon-1,17,17,17));font-size:.875rem!important;font-weight:700;line-height:1.571!important}.action slot[name=caption]::slotted(*){color:rgb(var(--colour-text-and-icon-2,72,72,72));display:block;font-size:.875rem;line-height:1.571}slot[name=trailing]{--skapa-price-font-size:0.875rem;align-self:var(--skapa-choice-trailing-alignment,center);color:rgb(var(--colour-text-and-icon-1,17,17,17));display:flex;flex-direction:column;flex-shrink:0;font-size:.875rem;font-weight:700;max-width:50%}slot[name=trailing]::slotted(*){display:block;line-height:1.571;margin-inline-start:1rem;text-align:end}slot[name=leading]::slotted(*){align-self:var(--skapa-choice-leading-alignment,center)}button:hover{--skapa-choice-item-border-colour:var(--skapa-choice-item-border-colour-hover,rgb(var(--colour-neutral-5,146,146,146)))}:host([selected]){--skapa-choice-item-border-colour:var(--skapa-choice-item-border-colour-selected,rgb(var(--colour-interactive-emphasised-border-selected,0,88,163)));--skapa-choice-item-border-colour-hover:var(--skapa-choice-item-border-colour-selected-hover,rgb(var(--colour-interactive-emphasised-border-selected,0,88,163)));--skapa-choice-item-border-width:2px}:host(:not([selected])) button:active{--skapa-choice-item-border-colour:rgb(var(--colour-neutral-7,17,17,17));--skapa-choice-item-border-colour-hover:rgb(var(--colour-neutral-7,17,17,17))}:host([disabled]){--skapa-choice-item-disabled-border-colour:rgb(var(--colour-interactive-disabled-1,204,204,204))}button:after{border:var(--skapa-choice-item-border-width,1px) solid var(--skapa-choice-item-disabled-border-colour,var(--skapa-choice-item-border-colour,rgb(var(--colour-neutral-3,223,223,223))));border-radius:4px;bottom:0;content:"";left:0;pointer-events:none;position:absolute;right:0;top:0}:focus-visible:after{box-shadow:0 0 0 4px var(--skapa-focus-ring-internal,rgb(var(--colour-neutral-1,255,255,255)));outline:2px solid var(--skapa-focus-ring-external,rgb(var(--colour-neutral-7,17,17,17)));outline-offset:4px;transition:outline .2s ease-out,box-shadow .2s ease-out}slot[name=leading]::slotted(*),slot[name=trailing]::slotted(*){flex-shrink:0}slot[name=leading]::slotted(skapa-image),slot[name=trailing]::slotted(skapa-image){color:#0000;height:3rem;max-width:3rem;object-fit:cover;padding:0;width:3rem}slot[name=leading]::slotted(skapa-icon),slot[name=trailing]::slotted(skapa-icon){color:rgb(var(--colour-text-and-icon-1,17,17,17))}slot[name=leading]::slotted(*){margin-inline-end:1rem}slot[name=nested]:not(.skapa-empty){display:block;padding:var(--skapa-choice-nested-padding,0 1.5rem 1.25rem)}@media (forced-colors:active){.action:disabled ::slotted(*){color:GrayText}}';
function generateTemplate({ html }) {
  const template = document.createElement("template");
  template.innerHTML = html`
    <style>
      ${styles}
    </style>
    <button type="button" class="action" id="action-btn">
      <slot name="leading"></slot>
      <slot class="content" name="content">
        <div class="text-content">
          <slot name="title"></slot>
          <slot name="caption"></slot>
          <slot name="addon"></slot>
        </div>
      </slot>
      <slot name="trailing"></slot>
    </button>
    <skapa-collapsible id="collapsible">
      <slot name="nested"></slot>
    </skapa-collapsible>
  `;
  return { main: template };
}
class ChoiceItem extends SkapaElement {
  constructor() {
    super();
    // Returns the assigned elements in the leading and trailing slots.
    this.getSlottedElements = () => {
      return this.leading.assignedElements({ flatten: true }).concat(this.trailing.assignedElements({ flatten: true }));
    };
    // Cached array of the assigned elements in the leading and trailing slots.
    this.slottedElements = new CachedValue(this.getSlottedElements);
    this.handleActionClick = () => {
      this.dispatchEvent(
        new CustomEvent("choice-item-select", { bubbles: true })
      );
    };
    this.handleSlotChange = (ev) => {
      this.slottedElements.updateCache = true;
      SlotTools.slotChange(ev);
      this.syncAssignedElements();
    };
    // Set assigned Price component and PaymentLogo size.
    this.syncAssignedElements = () => {
      const { prefixReplacer } = this.getPrefixService();
      this.slottedElements.value.forEach((element) => {
        switch (element.tagName.toLowerCase()) {
          case prefixReplacer("skapa-price"):
            element.setAttribute("size", "small");
            break;
          case prefixReplacer("skapa-payment-logo"):
            element.toggleAttribute("small", this.__size === "small");
            break;
          default:
        }
      });
    };
    /* Internal reference used only to propagate size sensitive configurations to potentially assigned elements, such as payment-logo.
      This setter is only meant to be called by the parent Choice component.
    */
    this.__size = "large";
    this.gatherProperties(["disabled", "selected"], this);
    const shadow = this.shadow = this.attachShadow({ mode: "open" });
    const templates = this.getTemplates(generateTemplate);
    shadow.append(templates.main.content.cloneNode(true));
    this.actionButton = shadow.querySelector(
      "#action-btn"
    );
    this.collapser = shadow.querySelector("#collapsible");
    this.leading = shadow.querySelector(
      "slot[name=leading]"
    );
    this.trailing = shadow.querySelector(
      "slot[name=trailing]"
    );
  }
  static get observedAttributes() {
    return ["disabled", "selected"];
  }
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("role", "listitem");
    this.actionButton.addEventListener("click", this.handleActionClick);
    this.actionButton.setAttribute("aria-pressed", this.selected.toString());
    this.syncAssignedElements();
    this.shadow.addEventListener("slotchange", this.handleSlotChange);
    Array.from(this.shadow.querySelectorAll("slot")).map(SlotTools.checkSlot);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.actionButton.removeEventListener("click", this.handleActionClick);
    this.shadow.removeEventListener("slotchange", this.handleSlotChange);
  }
  attributeChangedCallback(attributeName) {
    if (attributeName === "disabled" && this.actionButton) {
      this.actionButton.disabled = this.disabled;
      this.collapser.expanded = this.disabled ? false : this.selected;
    } else if (attributeName === "selected")
      this.collapser.expanded = this.selected;
  }
  get disabled() {
    return this.hasAttribute("disabled");
  }
  set disabled(val) {
    this.toggleAttribute("disabled", !!val);
  }
  get selected() {
    return this.hasAttribute("selected");
  }
  set selected(val) {
    this.toggleAttribute("selected", !!val);
    this.actionButton.setAttribute("aria-pressed", val.toString());
  }
  set size(size) {
    this.__size = size;
    this.syncAssignedElements();
  }
}
ChoiceItem.skapaTagName = "choice-item";
ChoiceItem.skapaDependencies = [Collapsible];
var ChoiceItem_default = ChoiceItem;
export {
  ChoiceItem,
  ChoiceItem_default as default
};
